// Bookings and calendar entries keep the business's wall clock: a day plus
// "HH:MM" times in the business's time zone, which the server turns into real
// instants with that zone (server/utils/appointmentTime.js). The dashboard
// reads them as local Dates that carry those wall-clock fields
// (getBookingStart, combineDateAndTime), so "now" and "today" have to be the
// business's wall clock too. The browser's own clock puts an owner in New York
// three hours ahead of their Los Angeles business.

const formatters = new Map();

// Throws for a zone the browser doesn't know, before anything is cached.
const wallClockFormatter = (timeZone) => {
  if (!formatters.has(timeZone)) {
    formatters.set(
      timeZone,
      new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        hourCycle: "h23",
      }),
    );
  }
  return formatters.get(timeZone);
};

// A zone this browser can use, or null. Without one the browser's own clock
// stands in, as it did before businesses sent their zone.
const usableTimeZone = (timeZone) => {
  const zone = typeof timeZone === "string" ? timeZone.trim() : "";
  if (!zone) return null;
  try {
    wallClockFormatter(zone);
    return zone;
  } catch (error) {
    return null;
  }
};

// The zone's offset from UTC at `instant`. Offsets only change on a minute
// boundary, and the bookings list asks once per row per check, so each zone
// keeps the last minute's answer rather than formatting again.
const offsets = new Map();

const offsetAt = (zone, instant) => {
  const minute = Math.floor(instant.getTime() / 60000);
  const cached = offsets.get(zone);
  if (cached?.minute === minute) return cached.offset;
  const parts = {};
  wallClockFormatter(zone)
    .formatToParts(instant)
    .forEach(({ type, value }) => {
      parts[type] = Number(value);
    });
  const wallAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  const offset = wallAsUtc - Math.floor(instant.getTime() / 1000) * 1000;
  offsets.set(zone, { minute, offset });
  return offset;
};

// The business's wall clock at `now`, as a local Date with the same fields, so
// it compares directly with getBookingStart / getBookingEnd and calendar-entry
// times. With no usable zone it is the browser's clock.
export const zonedNow = (timeZone, now = new Date()) => {
  const instant = now instanceof Date ? now : new Date(now);
  const zone = usableTimeZone(timeZone);
  if (!zone) return new Date(instant.getTime());
  const wall = new Date(instant.getTime() + offsetAt(zone, instant));
  return new Date(
    wall.getUTCFullYear(),
    wall.getUTCMonth(),
    wall.getUTCDate(),
    wall.getUTCHours(),
    wall.getUTCMinutes(),
    wall.getUTCSeconds(),
    wall.getUTCMilliseconds(),
  );
};

// "Central Time" for America/Chicago. Empty when there's no zone or the
// browser can't name it.
export const describeTimeZone = (timeZone) => {
  const zone = typeof timeZone === "string" ? timeZone.trim() : "";
  if (!zone) return "";
  try {
    const part = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      timeZoneName: "longGeneric",
    })
      .formatToParts(new Date())
      .find((entry) => entry.type === "timeZoneName");
    return part?.value || "";
  } catch (error) {
    return "";
  }
};

// The business's zone by name ("Pacific Time"), but only while its clock reads
// differently from the browser's: that is when a bare "16:30" could be taken
// for the viewer's own time. Empty otherwise.
export const businessTimeZoneLabel = (timeZone, now = new Date()) => {
  const zone = usableTimeZone(timeZone);
  if (!zone) return "";
  const instant = now instanceof Date ? now : new Date(now);
  if (zonedNow(zone, instant).getTime() === instant.getTime()) return "";
  return describeTimeZone(zone) || zone;
};
